/// <amd-module name="Repos/Views/PullRequest/Discussion/Components/PullRequestDiscussion" />
import { translateDisplayNamesToStorageKeys } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { useStorageKeysInMentions } from "Mention/Common/Util";
import * as React from "react";
import { CommentThreadStatus, GitRepository } from "Repos/Common/Contracts/Git";
import { IPullRequestMarkdownToolbarContext } from "Repos/Common/Contracts/Markdown";
import { DiscussionStatus } from "Repos/Discussion/Common/DiscussionContracts";
import { DiscussionThread } from "Repos/Discussion/DiscussionThread";
import { IDiscussionMarkdownEditorProps, IDiscussionThreadProps } from "Repos/Discussion/DiscussionThread.Types";
import { tryReplacingAttachments, wrapWithAttachmentDropTarget } from "Repos/PullRequest/Attachments/Attachment";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { getMarkdownPreviewProps, getMarkdownTextFieldProps, getMarkdownToolbarProps } from "../../Markdown/DiscussionProps";
import { hasPermission, PullRequestPermissions } from "../../PullRequestPermissionService";
import { IGitPullRequestCommentThreadModel } from "../PullRequestDiscussionThreadService";
import { getPullRequestDiscussionUIService, IPullRequestDiscussionContext } from "../PullRequestDiscussionUIService";

export interface IPullRequestDiscussionProps
    extends Pick<IDiscussionThreadProps, "author" | "className" | "commentEditorClassName" | "permissions" | "personaProvider"> {
    /**
     * Called when a new thread is published.
     */
    onPublishThread?: (newThreadId: number, tempThreadId: number) => void;

    /**
     * Called when a new comment is added to a thread.
     */
    onAddComment?: (threadId: number) => void;

    /**
     * Called when all the comments in a thread are deleted.
     */
    onDeleteThread?: (threadId: number) => void;

    /**
     * Called when a comments in a thread is deleted.
     */
    onDeleteComment?: (threadId: number) => void;

    /**
     * Called when a comment is cancelled.
     */
    onCancelComment?: (threadId: number) => void;

    /**
     * Called when a newly started discussion thread is cancelled.
     */
    onCancelThread?: (threadId: number) => void;

    /**
     * Called when the status of the thread changes.
     */
    onChangeStatus?: (status: CommentThreadStatus) => void;

    /**
     * Called when a comment is updated.
     */
    onUpdateComment?: (threadId: number) => void;

    /**
     * Id of the pull request where discussion is happening.
     */
    pullRequestId: number;

    /**
     * Repository of the pull request.
     */
    repository: GitRepository;

    /**
     * Discussion thread to display.
     */
    thread: IGitPullRequestCommentThreadModel;

    isShim?: boolean;
}

export class PullRequestDiscussion extends VssComponent<IPullRequestDiscussionProps> {
    public render(): JSX.Element {
        const {
            author,
            className,
            commentEditorClassName,
            isShim,
            onCancelComment,
            onCancelThread,
            permissions,
            personaProvider,
            pullRequestId,
            repository,
            thread
        } = this.props;

        const { pageContext } = this.context;

        const pullRequestMarkdownContext: IPullRequestMarkdownToolbarContext = {
            projectId: repository.project.id,
            pullRequestId,
            repositoryId: repository.id
        };
    
        const getMarkdownEditorProps = (commentId: number): IDiscussionMarkdownEditorProps => {
            return {
                previewProps: {
                    ...getMarkdownPreviewProps(
                        pageContext,
                        thread,
                        newContent => {
                            const comment = commentId < 0 ? thread.getInputComment() : thread.getComments().value.find(c => c.getId() === commentId);
                            if (comment) {
                                comment.setContent(newContent);
                                if (commentId > 0 && !comment.isEditing().value) {
                                    getPullRequestDiscussionUIService(this.context.pageContext).saveThread(this.getDiscussionContext());
                                }
                            }
                        },
                        () => {
                            if (!hasPermission(pageContext, PullRequestPermissions.AddEditComment)) {
                                return false;
                            }

                            const comment = commentId < 0 ? thread.getInputComment() : thread.getComments().value.find(c => c.getId() === commentId);
                            if (comment) {
                                const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData()!;
                                return comment.isOwner(pageData.user);
                            }

                            return false;
                        }
                    )
                },
                textFieldProps: { ...getMarkdownTextFieldProps(pageContext, pullRequestMarkdownContext) },
                wrapTextField: (textField, textSourceRef) =>
                    wrapWithAttachmentDropTarget(pageContext, pullRequestMarkdownContext, textField, textSourceRef),
                toolbarProps: { ...getMarkdownToolbarProps(pageContext, pullRequestMarkdownContext, thread) }
            };
        };

        return (
            <DiscussionThread
                author={author}
                className={className}
                commentEditorClassName={commentEditorClassName}
                getMarkdownEditorProps={commentId => getMarkdownEditorProps(commentId)}
                permissions={permissions}
                personaProvider={personaProvider}
                onAddComment={this.onAddComment}
                onChangeStatus={this.onChangeStatus}
                onDismiss={onCancelThread}
                onDismissComment={onCancelComment}
                onLikeComment={this.onLikeComment}
                onRemoveComment={this.onRemoveComment}
                onUpdateComment={this.onUpdateComment}
                showLikes={true}
                thread={thread}
                isShim={isShim}
                baseUrl={this.getPullRequestURL()}
            />
        );
    }

    private getPullRequestURL(): string {
        return this.props.repository.webUrl + "/pullRequest/" + this.props.pullRequestId;
    }

    private onAddComment = async (commentId: number, comment: string): Promise<void> => {
        const { onPublishThread } = this.props;
        comment = await this.performTranslations(comment);
        getPullRequestDiscussionUIService(this.context.pageContext).addComment(
            this.getDiscussionContext(),
            commentId,
            comment,
            onPublishThread,
            this.props.onAddComment
        );
    };

    private onChangeStatus = (status: DiscussionStatus): void => {
        const { onChangeStatus } = this.props;
        getPullRequestDiscussionUIService(this.context.pageContext).changeStatus(this.getDiscussionContext(), status, onChangeStatus);
    };

    private onLikeComment = (commentId: number, like: boolean, identity: IdentityRef): void => {
        getPullRequestDiscussionUIService(this.context.pageContext).likeComment(this.getDiscussionContext(), commentId, like, identity);
    };

    private onRemoveComment = (commentId: number): void => {
        const { onDeleteComment, onDeleteThread } = this.props;
        getPullRequestDiscussionUIService(this.context.pageContext).removeComment(
            this.getDiscussionContext(),
            commentId,
            onDeleteThread,
            onDeleteComment
        );
    };

    private onUpdateComment = async (commentId: number, comment: string): Promise<void> => {
        const { onUpdateComment } = this.props;
        comment = await this.performTranslations(comment);
        getPullRequestDiscussionUIService(this.context.pageContext).updateComment(this.getDiscussionContext(), commentId, comment, onUpdateComment);
    };

    private performTranslations = async (comment: string): Promise<string> => {
        const { pullRequestId, repository } = this.props;
        comment = await tryReplacingAttachments(this.context.pageContext, comment, {
            projectId: repository.project.id,
            pullRequestId,
            repositoryId: repository.id
        });
        return useStorageKeysInMentions(this.context.pageContext) ? translateDisplayNamesToStorageKeys(this.context.pageContext, comment) : comment;
    };

    private getDiscussionContext(): IPullRequestDiscussionContext {
        const { pullRequestId, repository, thread } = this.props;

        return {
            project: repository.project.id,
            repositoryId: repository.id,
            pullRequestId,
            thread
        };
    }
}
